<template>
  <div>
    <div class="col-6 scroll-form">
      <Draggable
        v-model="layout"
        class="draggable row no-gutters align-items-center"
        ghost-class="ghost"
        :class="{ moving }"
        @start="moving = true"
        @end="moving = false"
      >
        <div
          v-for="field in layout"
          :key="field.id"
          class="field-section"
          :class="{
            selected: selectedField && field.id === selectedField.id
          }"
        >
          <div class="d-flex align-items-center">
            <div
              v-if="field.type == 'statictext'"
              style="width: 100%"
              :style="{
                'font-weight': field.bold ? '500' : undefined,
              }"
            >
              <span style="white-space: pre-wrap;">{{ field.title }}</span>
              <hr
                v-if="field.separator"
                class="mb-0"
              >
            </div>
            <div
              v-else
              class="flex-grow-1"
            >
              <div class="d-flex justify-content-between">
                <label>
                  <span v-if="field.type !== 'checkbox'">
                    {{ field.title }}
                  </span>
                </label>
                <div class="small">
                  {{ field.code }}
                </div>
              </div>
              <div v-if="field.type === 'textinput'">
                <BFormInput
                  v-model="exampleData[field.id]"
                  size="sm"
                  :placeholder="field.title"
                />
              </div>
              <div v-else-if="field.type === 'textareainput'">
                <BFormTextarea
                  v-model="exampleData[field.id]"
                  size="sm"
                  :placeholder="field.title"
                />
              </div>
              <div v-else-if="field.type === 'checkbox'">
                <BFormCheckbox
                  v-model="exampleData[field.id]"
                  class="form-checkbox"
                  :placeholder="field.title"
                  style="margin-left: -10px"
                >
                  {{ field.title }}
                </BFormCheckbox>
              </div>
              <div v-else-if="field.type === 'dropdown'">
                <BFormSelect
                  :key="(field.options || []).length"
                  v-model="exampleData[field.id]"
                  :placeholder="field.title"
                  :options="field.options || []"
                />
              </div>
              <div v-else-if="field.type === 'fileinput'">
                <BFormFile
                  size="sm"
                  :browse-text="$t('forms.forms.browse')"
                  :placeholder="$t('forms.forms.fileinput')"
                  disabled
                />
              </div>
            </div>
            <div
              v-if="selectedField && field.id === selectedField.id"
              class="field-edit selected"
            >
              <i class="fas fa-arrow-right" />
            </div>
            <div
              v-else
              class="field-edit"
              @click="selectedField = field"
            >
              <i class="fas fa-pen" />
            </div>
          </div>
          <div
            v-if="(field.attachments || []).length"
            class="field-attachments mt-1 pl-1"
          >
            <a
              v-for="attachment in field.attachments"
              :key="attachment.key || attachment"
              :href="attachmentUrl(attachment)"
              target="_blank"
              rel="noopener noreferrer"
              class="field-attachment-link d-inline-flex align-items-center mr-3"
            >
              <i class="fas fa-file-alt text-muted mr-1" />
              <span class="small text-truncate">{{ attachmentLabel(attachment) }}</span>
            </a>
          </div>
        </div>
      </Draggable>
      <div class="mt-3">
        <button
          class="btn btn-sm btn-block btn-primary field-add"
          @click="addField"
        >
          <i class="fas fa-add pr-2" />
          {{ $t('general.add') }}
        </button>
      </div>
    </div>
    <div class="col-6 scroll-form">
      <div
        v-if="selectedField"
        class="edit-section"
      >
        <div class="d-flex justify-content-between">
          <div class="font-weight-bold">
            {{ $t('forms.forms.editField') }}
          </div>
          <button
            type="button"
            aria-label="Close"
            class="close"
            style="font-size: 30px"
            @click="selectedField = null"
          >
            ×
          </button>
        </div>

        <label class="pt-3">
          {{ $t('forms.forms.fieldDescription') }}
        </label>
        <BFormTextarea
          v-if="selectedField.type == 'statictext'"
          v-model="selectedField.title"
          :placeholder="$t('forms.forms.fieldDescription')"
        />
        <BFormInput
          v-else
          v-model="selectedField.title"
          :placeholder="$t('forms.forms.fieldDescription')"
        />

        <label
          v-if="selectedField.type != 'statictext'"
          class="pt-3"
        >
          {{ $t('forms.forms.fieldCode') }}
        </label>
        <BFormInput
          v-if="selectedField.type != 'statictext'"
          v-model="selectedField.code"
          :placeholder="$t('forms.forms.fieldCode')"
          pattern="[a-zA-Z0-9]*"
          @blur="selectedField.code = selectedField.code.replace(/[^a-zA-Z0-9]/g, '')"
        />

        <BFormCheckbox
          v-if="selectedField.type != 'statictext' && formType === 'ShopListing'"
          v-model="selectedField.canUpdate"
          class="pt-3"
        >
          {{ $t('forms.forms.fieldCanUpdate') }}
        </BFormCheckbox>

        <label class="pt-3">
          {{ $t('forms.forms.fieldType') }}
        </label>
        <BFormSelect
          v-model="selectedField.type"
          :options="fieldTypes"
          :placeholder="$t('forms.forms.fieldType')"
        />
        <template v-if="selectedField.type === 'dropdown'">
          <label class="pt-3">
            {{ $t('forms.forms.options') }}
          </label>
          <BFormTextarea
            :value="(selectedField.options || []).join('\n')"
            :placeholder="$t('forms.forms.options')"
            @update="$set(selectedField, 'options', $event.split('\n'))"
          />
        </template>

        <template v-if="selectedField.type == 'statictext'">
          <BFormCheckbox
            v-model="selectedField.bold"
            class="pt-3"
          >
            {{ $t('general.title') }}
          </BFormCheckbox>
          <BFormCheckbox
            v-model="selectedField.separator"
            class="pt-3"
          >
            {{ $t('forms.separator') }}
          </BFormCheckbox>
        </template>
        <template v-if="selectedField.type != 'statictext'">
          <hr>
          <label>
            {{ $t('forms.forms.validationScript') }}
          </label>
          <BFormInput
            v-model="selectedField.validationScript"
            size="sm"
            :placeholder="$t('forms.forms.validationScript')"
          />
          <label
            class="pt-3"
          >
            {{ $t('forms.forms.validationMessage') }}
          </label>
          <BFormInput
            v-model="selectedField.validationMessage"
            size="sm"
            :placeholder="$t('forms.forms.validationMessage')"
          />
        </template>
        <hr class="mb-0">

        <template v-if="formId">
          <label class="pt-3">
            {{ $t('forms.forms.fieldAttachments') }}
          </label>
          <input
            ref="attachmentFileInput"
            type="file"
            class="d-none"
            @change="onAttachmentFileSelected"
          >
          <div
            v-for="(attachment, idx) in (selectedField.attachments || [])"
            :key="attachment.key || idx"
            class="d-flex align-items-center mt-2"
          >
            <i class="fas fa-file-alt text-muted mr-2" />
            <BFormInput
              v-model="attachment.name"
              size="sm"
              class="flex-grow-1"
              :placeholder="fileLabelFromKey(attachment.key)"
            />
            <button
              type="button"
              class="btn btn-sm btn-link text-danger ml-1 p-1"
              style="min-width: 30px;"
              @click="removeAttachment(idx)"
            >
              <i class="fas fa-trash" />
            </button>
          </div>
          <div class="d-flex align-items-center flex-wrap mt-2">
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              :disabled="attachmentUploading"
              @click="openAttachmentPicker"
            >
              <i
                class="fas fa-upload pr-1"
              />
              {{ $t('forms.forms.addAttachment') }}
            </button>
          </div>
        </template>

        <label
          class="pt-3"
        >
          {{ $t('forms.forms.showOnlyWhen') }}
        </label>
        <div class="d-flex">
          <BFormSelect
            v-model="selectedField.showOnlyWhenField"
            class="w-50 mr-2"
            :options="layoutCodes"
            :placeholder="$t('forms.forms.showOnlyWhen')"
          />
          <BFormInput
            v-model="selectedField.showOnlyWhenValue"
            class="w-50 ml-2"
            :placeholder="$t('forms.forms.showOnlyWhenValue')"
          />
        </div>
        <hr>
        <div>
          <button
            class="btn btn-secondary btn-sm"
            @click="layout = layout.filter(x => x.id !== selectedField.id); selectedField = null"
          >
            <i class="fa-solid fa-trash-can pr-1" />
            {{ $t('general.remove') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import axios from '@/api/axios';
import Draggable from 'vuedraggable';

export default {
  props: {
    value: Array,
    formAttachments: Array, // { key, url }
    formId: String,
    formType: String,
  },
  data: () => ({
    layout: [],
    nextId: 1,
    moving: false,
    exampleData: {},
    layoutOrder: [],
    selectedField: null,
    attachmentUploading: false,
    attachmentKeysSig: undefined,
  }),
  components: {
    Draggable,
  },
  computed: {
    layoutCodes() {
      return this.layout
        .filter((x) => x.code)
        .map((x) => x.code)
        .concat(['']);
    },
    fieldTypes() {
      return [
        'textinput',
        'textareainput',
        'checkbox',
        'dropdown',
        'fileinput',
        'statictext',
      ]
        .map((t) => ({
          value: t,
          text: this.$t(`forms.forms.${t}`),
        }));
    },
  },
  watch: {
    layout(v) {
      this.$emit('update:value', v);
    },
  },
  methods: {
    attachmentUrl(attachment) {
      const key = attachment?.key || attachment;
      if (!key) return '#';
      return this.formAttachments.find((a) => a.key === key)?.url || '#';
    },
    attachmentLabel(attachment) {
      if (typeof attachment === 'string') return this.fileLabelFromKey(attachment);
      return attachment.name || this.fileLabelFromKey(attachment.key);
    },
    fileLabelFromKey(key) {
      if (!key) return '';
      const parts = key.split('/');
      return parts[parts.length - 1] || key;
    },
    removeAttachment(idx) {
      this.selectedField.attachments.splice(idx, 1);
    },
    openAttachmentPicker() {
      if (this.$refs.attachmentFileInput) {
        this.$refs.attachmentFileInput.click();
      }
    },
    onAttachmentFileSelected(event) {
      const input = event.target;
      const file = input.files && input.files[0];
      input.value = '';
      if (!file || !this.selectedField || !this.formId) return;

      this.attachmentUploading = true;
      const formData = new FormData();
      formData.append('File', file);
      formData.append('FormId', this.formId);

      axios
        .post('forms/attachment', formData)
        .then(({ data }) => {
          if (!Array.isArray(this.selectedField.attachments)) {
            this.$set(this.selectedField, 'attachments', []);
          }

          this.selectedField.attachments.push({ key: data.key, name: file.name });
          this.$emit('newAttachment', data);
        })
        .finally(() => {
          this.attachmentUploading = false;
        });
    },
    addField() {
      const entry = {
        id: this.nextId,
        title: '',
        type: 'textinput',
        attachments: [],
      };

      this.nextId += 1;
      this.layout.push(entry);
      this.selectedField = entry;
    },
  },
  created() {
    this.layout = this.value;
    this.nextId = this.layout.reduce((acc, curr) => Math.max(curr.id || 0, acc), 0) + 1;
  },
};
</script>

<style lang="scss" scoped>
  .field-add {
    max-width: 200px;
    margin: auto;
  }

  .edit-section {
    border: 1px solid #eee;
    padding: 15px;
    margin-left: 30px;
    border-radius: 15px;
  }

  .field-edit {
    padding-left: 5px;
    align-self: center;
    padding: 5px 10px;
    cursor: pointer;
    margin-left: 15px;
    background-color: #f6f6f6;
    border-radius: 50%;
    width: 35px;
    height: 35px;
    text-align: center;

    &:hover {
      background-color: #ddd;
    }

    &.selected {
      background-color: #ddd;
    }
  }

  .form-checkbox::v-deep .custom-control-label {
    font-weight: normal;
    text-transform: none;
    padding-top: 2px;
  }

  .ghost {
    opacity: 0.5;
    background: rgba($blue, 0.2);
    cursor: grabbing;
  }

  .moving * {
    cursor: grabbing;
  }

  .scroll-form {
    height: calc(100vh - 220px);
    padding-bottom: 40px;
    overflow-y: auto;
  }

  .field-section {
    padding: 10px;
    width: 100%;
    border-left: 2px solid transparent;

    &.selected {
      background-color: rgba($blue, 0.1);
      border-left: 2px solid $blue;
    }
  }

  .field-attachments {
    width: 100%;
  }

  .field-attachment-link {
    max-width: 100%;
    color: inherit;
    text-decoration: none;

    &:hover {
      color: $blue;
      text-decoration: underline;
    }
  }
</style>
